import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search, ShieldCheck, UserPlus, Users, UserX, X } from 'lucide-react';
import { problemDetail } from '@/shared/api/problem';
import {
  getGetApiV1UsersQueryKey,
  useDeleteApiV1UsersUserIdRolesRole,
  usePutApiV1UsersUserIdSuspension,
  useGetApiV1Users,
  usePostApiV1UsersUserIdRoles,
} from '@/shared/api/generated/users/users';
import type { GetApiV1UsersParams, UserSummaryDto } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { QueryError } from '@/shared/ui/QueryError';
import { Chip } from '@/shared/ui/Chip';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Pagination } from '@/shared/ui/Pagination';
import { TableSkeleton } from '@/shared/ui/TableSkeleton';
import { EmptyState } from '@/shared/ui/EmptyState';
import { RemoveRoleDialog } from '@/features/users/ui/RemoveRoleDialog';

/**
 * ⚠️ Roles that decide whether somebody reaches a missing person's position are marked, and the
 * rest are not.
 *
 * §12.3 restricts that data and §23.1 spreads it across roles unevenly: Head of Control and Desk
 * Control follow live missions, the system administrator reads identity documents, and a field
 * volunteer is sent to the coordinates themselves. A committee member votes on a summary and an
 * association employee reads statistics — real roles, but not ones that put a person's location on
 * somebody's screen.
 *
 * An administrator scanning a list of names needs that difference visible without reading each row
 * twice, which is what the chip tone carries.
 */
const SENSITIVE_ROLES = new Set([
  'system-administrator',
  'head-of-control',
  'desk-control',
  'field-volunteer',
]);

/**
 * Appointing people to roles (§23.1).
 *
 * ⚠️ **Nothing here existed until 21 August 2026, and the gap was not cosmetic.** Roles could be
 * granted in exactly two places — the deployment's `Bootstrap:AdministratorPhone` setting and
 * volunteer approval, which grants field-volunteer and nothing else. Seven of the nine roles could
 * not be given at all, so an association could not form a control room, seat its committee of
 * twenty-seven, or name a field leader. Found while deploying the trial.
 *
 * ⚠️ **Composed from the panel's own layout pieces**: PageHeader, Panel, Table, Select, Field,
 * Button, Chip. An earlier version of this screen drew loose controls onto the page with hand-made
 * classes and no card around them, and it read as unfinished beside every other screen — because it
 * was the only one not built from the same parts.
 *
 * ⚠️ **Every action is behind the server's Admin policy, and the panel does not re-decide that.**
 * §12.2 keeps visibility data-driven: an operator without the role gets 403 and reads the API's own
 * sentence.
 *
 * Server state comes from the generated hooks only (§5.2, §5.3).
 */
export function RoleManager() {
  const { t } = useLocale();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // The removal being confirmed. Held as the whole pair rather than ids so the dialog can name both
  // the person and the role — the overlay hides the row that would otherwise say who.
  const [removing, setRemoving] = useState<{ user: UserSummaryDto; role: string } | null>(null);

  // Typed as the generated params, so a filter this panel invents that the API does not accept is a
  // compile error rather than a silently ignored query string.
  const PAGE_SIZE = 20;

  const params: GetApiV1UsersParams = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(role !== 'all' ? { role } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const users = useGetApiV1Users(params);

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: getGetApiV1UsersQueryKey(params) });
  };

  const onError = (cause: unknown) => {
    // ⚠️ The API's own sentence first (§13's `detail`). It distinguishes «لا يمكنك سحب صلاحية
    // الإدارة من نفسك» from «لا يمكن سحب صلاحية الإدارة من آخر مدير للنظام» — two refusals with two
    // different remedies, where a generic "forbidden" would send an administrator hunting.
    setError(problemDetail(cause) ?? t.users.changeFailed);
  };

  const grant = usePostApiV1UsersUserIdRoles({ mutation: { onSuccess: refresh, onError } });
  const revoke = useDeleteApiV1UsersUserIdRolesRole({ mutation: { onSuccess: refresh, onError } });

  /// ⚠️ **Stopping an account, which nothing in the platform could do until 10 September 2026.**
  /// The sign-in path has always refused a suspended account a code, a verification and a refresh
  /// (§12.1) and the callout audience has always skipped one — and `User.Suspend()` had no caller,
  /// so an association could accept a volunteer and refuse a volunteer and could not stop one it
  /// had already accepted.
  const setSuspension = usePutApiV1UsersUserIdSuspension({
    mutation: { onSuccess: refresh, onError },
  });

  const roleNames = t.users.roleNames as Record<string, string>;
  const allRoles = Object.keys(roleNames);
  const label = (name: string) => roleNames[name] ?? name;

  const busy = grant.isPending || revoke.isPending || setSuspension.isPending;
  const filtered = search.trim() !== '' || role !== 'all';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t.users.heading}
        lede={t.users.lede}
        actions={
          users.data ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-elevated px-3 py-1 text-sm text-muted-foreground">
              <Users aria-hidden className="size-4" />
              <span className="tabular-nums">{users.data.totalCount}</span>
            </span>
          ) : null
        }
      />

      <Panel
        title={t.users.filters}
        actions={
          filtered ? (
            // Only when something is actually filtered — a permanent "clear" beside untouched
            // controls is a button that does nothing, which is how an operator learns to ignore the
            // row of controls entirely.
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setRole('all');
                setPage(1);
              }}
            >
              <X aria-hidden className="size-3.5" />
              {t.users.clearFilters}
            </Button>
          ) : null
        }
      >
        {/* ⚠️ A grid, not `flex … items-end`.
            Two things were wrong with the row this replaces. `items-end` aligned the two fields by
            their BOTTOMS, and only one of them carries a hint — so the search label and input sat a
            full line above the role label and select, which is what made the card read as broken.
            And both controls had fixed widths (`w-72`, `w-56`) on a card 1100px wide, leaving two
            thirds of it empty with the controls huddled at one edge.
            A grid aligns the tops, fills the width, and drops to one column on a narrow screen. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.users.search} htmlFor="users-search" hint={t.users.searchHint}>
            <div className="relative">
              {/* Logical inset, never `left-`: §5.6 flips this panel between Arabic and English at
                  runtime, and a magnifier pinned to the left lands on top of Arabic text. */}
              <Search
                aria-hidden
                className="pointer-events-none absolute inset-inline-start-4 top-1/2 size-4 -translate-y-1/2 text-gray-400"
              />
              <input
                id="users-search"
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t.users.searchPlaceholder}
                // ⚠️ `w-full`, not `w-72`: the cell decides the width now, so the field grows with
                // the card instead of leaving a gap beside it. The rest matches the template's own
                // inputs — an 11-unit height, a hairline border, and a focus ring in the accent —
                // because a search box styled unlike every other field on the screen reads as a
                // different kind of control.
                className="h-11 w-full rounded-md border border-gray-200 bg-transparent py-2.5 pe-4 ps-11 text-theme-sm text-gray-800 shadow-theme-xs outline-hidden placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
              />
            </div>
          </Field>

          <Field label={t.users.roleFilter} htmlFor="users-role-filter">
            {/* ⚠️ Every filter resets the page. Narrowing while on page seven of an unfiltered
                list lands on an empty table that reads as a broken filter — the operator's next
                move is to clear the thing that was working. */}
            <Select
              value={role}
              onValueChange={(next) => {
                setRole(next);
                setPage(1);
              }}
            >
              <SelectTrigger id="users-role-filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.users.filterAll}</SelectItem>
                {allRoles.map((name) => (
                  <SelectItem key={name} value={name}>
                    {label(name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Panel>

      {error ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-md border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          <ShieldCheck aria-hidden className="size-4 shrink-0" />
          {error}
        </p>
      ) : null}

      {/* ⚠️ `p-0`: the table draws its own gutters and the pagination bar its own rule. Left
          on the card default, every cell sat 24px inside a card that was already padded, and
          the rule above the pager floated in the middle of the padding instead of spanning
          the card. */}
      <Panel title={t.users.listTitle} bodyClassName="p-0">
        {users.isPending ? (
          // ⚠️ A skeleton the shape of the table, not a line of text: the page used to assemble
          // itself at whatever height the data turned out to be, moving controls out from under a
          // pointer already reaching for them.
          <TableSkeleton rows={5} columns={5} />
        ) : users.isError ? (
          // ⚠️ QueryError refuses to offer a retry on a 403 — the case that matters here, because an
          // operator without the Admin policy can reach this route and must be told why rather than
          // handed a button guaranteed to fail.
          <QueryError error={users.error} onRetry={() => void users.refetch()} />
        ) : users.data.items.length === 0 ? (
          // ⚠️ An empty queue and an over-narrow filter look identical in a line of grey text, and
          // only one of them has a way out. The action offered here IS the way out.
          <EmptyState
            icon={UserX}
            title={t.users.empty}
            hint={filtered ? t.users.emptyFiltered : t.users.emptyAll}
            action={
              filtered ? (
                <Button
                  variant="ghost"
                  className="px-3 py-1.5 text-xs"
                  onClick={() => {
                    setSearch('');
                    setRole('all');
                    setPage(1);
                  }}
                >
                  {t.users.clearFilters}
                </Button>
              ) : null
            }
          />
        ) : (
          <Table className="min-w-3xl">
            {/* ⚠️ TableHeader is the <thead>; TableHead is a <th>. Swapped, every column heading
                renders as a row of its own stacked above the table — which is what production
                showed before this was corrected, with ten tests green. */}
            {/* ⚠️ **The columns are given widths, because the browser gives them the wrong ones.**
                Left to size themselves, the table hands space to whatever content is widest — the
                row of role chips and a 176px select — and squeezes what is left onto the name.
                So the one column an operator scans down, looking for a colleague, was the narrowest
                on the screen and wrapped every name onto two lines, while a column holding one
                button sat half empty beside it.

                A name is the row's identity: it gets a floor and never wraps. The roles column is
                the elastic one, because it is the only cell whose contents genuinely vary. */}
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="w-[22%] min-w-44">
                  {t.users.name}
                </TableHead>
                <TableHead scope="col" className="w-[18%]">
                  {t.users.phone}
                </TableHead>
                <TableHead scope="col" className="w-[12%]">
                  {t.users.region}
                </TableHead>
                <TableHead scope="col">{t.users.roles}</TableHead>
                <TableHead scope="col" className="w-32">
                  {t.users.account}
                </TableHead>
                <TableHead scope="col" className="w-56 text-end">
                  {t.users.add}
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {users.data.items.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>
                    <div className="flex flex-col">
                      {/* ⚠️ `truncate`, not wrap: a name broken across two lines makes every row a
                          different height, and a column of ragged rows is one an eye cannot run
                          down. The full name is in `title` for the rare one that is too long. */}
                      <span className="truncate font-medium text-gray-800" title={user.fullName}>
                        {user.fullName}
                      </span>
                      <span className="truncate text-theme-xs text-gray-500">{user.city}</span>
                    </div>
                  </TableCell>

                  {/* Left to right whatever the interface language: a number read aloud digit by
                      digit is misread when its order is mirrored. */}
                  <TableCell dir="ltr" className="text-start tabular-nums text-muted-foreground">
                    {user.phone}
                  </TableCell>

                  <TableCell className="text-muted-foreground">{user.region}</TableCell>

                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {user.roles.length === 0 ? (
                        <span className="text-xs text-muted-foreground">{t.users.noRoles}</span>
                      ) : (
                        user.roles.map((name) => (
                          <span
                            key={name}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-elevated ps-0.5 pe-1"
                          >
                            <Chip tone={SENSITIVE_ROLES.has(name) ? 'approved' : 'neutral'}>
                              {label(name)}
                            </Chip>

                            {/* An icon button, so a row carrying four roles is four small marks
                                rather than four repetitions of the word "سحب" competing with the
                                role names an operator is actually scanning. */}
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setRemoving({ user, role: name })}
                              aria-label={`${t.users.remove} — ${label(name)}`}
                              title={t.users.remove}
                              className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-danger/10 hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <X aria-hidden className="size-3.5" />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </TableCell>

                  {/* ⚠️ **Whether this person can sign in at all**, and the control that changes it.
                      A stopped account keeps everything §23.9 requires — its tracks, reports and
                      certificates all stand — and letting it back is the same button. */}
                  <TableCell>
                    {user.status === 'Suspended' ? (
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`restore-${user.userId}`}
                        onClick={() =>
                          setSuspension.mutate({ userId: user.userId, data: { suspended: false } })
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-danger/40 px-2 py-0.5 text-xs text-danger transition-colors hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {t.users.suspended}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`suspend-${user.userId}`}
                        onClick={() =>
                          setSuspension.mutate({ userId: user.userId, data: { suspended: true } })
                        }
                        className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-danger hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {t.users.suspend}
                      </button>
                    )}
                  </TableCell>

                  <TableCell className="text-end">
                    {/* ⚠️ Only the roles this person does NOT hold. Offering one they already have
                        invites a press that changes nothing, and a control that does nothing when
                        pressed is one an operator stops trusting under time pressure.

                        ⚠️ `value=""` keeps this a command rather than a field: it shows the same
                        prompt after every grant instead of holding the role last chosen, which on a
                        row that now HAS that role would read as a stale selection. */}
                    <Select
                      value=""
                      disabled={busy}
                      onValueChange={(chosen) =>
                        grant.mutate({ userId: user.userId, data: { role: chosen } })
                      }
                    >
                      <SelectTrigger
                        className="ms-auto w-full max-w-44"
                        aria-label={`${t.users.add} — ${user.fullName}`}
                      >
                        <span className="flex items-center gap-2">
                          <UserPlus aria-hidden className="size-4 text-muted-foreground" />
                          <SelectValue placeholder={t.users.add} />
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {allRoles
                          .filter((name) => !user.roles.includes(name))
                          .map((name) => (
                            <SelectItem key={name} value={name}>
                              {label(name)}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {users.data ? (
          <Pagination
            page={users.data.page}
            pageSize={users.data.pageSize}
            totalCount={users.data.totalCount}
            onPageChange={setPage}
          />
        ) : null}
      </Panel>

      <RemoveRoleDialog
        open={removing !== null}
        personName={removing?.user.fullName ?? ''}
        roleLabel={removing ? label(removing.role) : ''}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
        onConfirm={() => {
          const target = removing;
          setRemoving(null);
          if (target) revoke.mutate({ userId: target.user.userId, role: target.role });
        }}
      />
    </div>
  );
}
